
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchEvents, fetchAllEvents } from "@/services/eventService";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import EventPageHeader from "@/components/events/EventPageHeader";
import EventSearchAndFilter from "@/components/events/EventSearchAndFilter";
import EventTabView from "@/components/events/EventTabView";

const Events = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<"calendar" | "list" | "approvals">("calendar");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Use the appropriate fetch function based on view and admin status
  const { data: events, isLoading, error, refetch } = useQuery({
    queryKey: ['events', view === 'approvals' ? 'all' : 'approved'],
    queryFn: view === 'approvals' ? fetchAllEvents : fetchEvents,
    staleTime: 5000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();
        
        setIsAdmin(!!profile?.is_admin);
      }
    };
    
    checkAdmin();
  }, []);

  useEffect(() => {
    console.log("Events page mounted, refetching data...");
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (events) {
      console.log("Events loaded in Events.tsx:", events.length);
      console.log("Current view:", view);
    }
    if (error) {
      console.error("Error loading events:", error);
      toast.error("Failed to load events. Please try again.");
    }
  }, [events, error, view]);

  const handleAddEvent = () => {
    navigate("/events/add");
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Searching for events:", searchTerm);
    if (searchTerm.trim()) {
      refetch();
    }
  };

  // Apply both search and category filters
  const filteredEvents = events 
    ? events.filter(event => {
        const matchesSearch = !searchTerm.trim() || 
          event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (event.category && event.category.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesCategory = !categoryFilter || 
          event.category === categoryFilter;

        return matchesSearch && matchesCategory;
      })
    : [];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow container py-8">
        <EventPageHeader onAddEvent={handleAddEvent} />
        
        <EventSearchAndFilter
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearch={handleSearch}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
        />
        
        <EventTabView
          view={view}
          setView={setView}
          isLoading={isLoading}
          error={error}
          filteredEvents={filteredEvents}
          refetch={refetch}
          isAdmin={isAdmin}
        />
      </main>
      
      <footer className="bg-gray-100 py-6">
        <div className="container text-center text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Ourcelium. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Events;
